import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Calendar, Clock, CheckCircle, Truck, ArrowLeft, RotateCcw, RefreshCw } from 'lucide-react';
import { useCart, Order } from '../contexts/CartContext';
import { useVoice } from '../contexts/voice';
import { useLanguage } from '../contexts/LanguageContext';

const statusConfig: Record<string, { icon: typeof Clock; color: string; bgColor: string }> = {
  processing: {
    icon: Clock,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
  },
  shipped: {
    icon: Truck,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  delivered: {
    icon: CheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  returned: {
    icon: RotateCcw,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
  return_requested: {
    icon: RefreshCw,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
  },
  pickup_scheduled: {
    icon: RefreshCw,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
  },
};

const defaultStatusStyle = {
  icon: Package,
  color: 'text-gray-500',
  bgColor: 'bg-gray-500/10',
};

// Parse Dutch date string like "maandag 2 december 2024" to Date
function parseDutchDate(dateStr: string): Date | null {
  const months: Record<string, number> = {
    januari: 0, februari: 1, maart: 2, april: 3, mei: 4, juni: 5,
    juli: 6, augustus: 7, september: 8, oktober: 9, november: 10, december: 11
  };

  const parts = dateStr.toLowerCase().split(' ');
  if (parts.length < 4) return null;

  const day = parseInt(parts[1]);
  const month = months[parts[2]];
  const year = parseInt(parts[3]);

  if (isNaN(day) || month === undefined || isNaN(year)) return null;
  return new Date(year, month, day);
}

function getEffectiveStatus(order: Order): string {
  // If order has a return status, use that
  if (order.status === 'returned' || order.status === 'return_requested') {
    return order.status;
  }

  const deliveryDate = parseDutchDate(order.deliveryDate);
  if (!deliveryDate) return order.status;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  deliveryDate.setHours(0, 0, 0, 0);

  if (today >= deliveryDate) {
    return 'delivered';
  }
  return order.status;
}

function OrderCard({ order, t }: { order: Order; t: (key: string, params?: Record<string, string | number>) => string }) {
  const effectiveStatus = getEffectiveStatus(order);
  const statusStyle = statusConfig[effectiveStatus] || defaultStatusStyle;
  const StatusIcon = statusStyle.icon;
  const statusLabel = t(`status.${effectiveStatus}`) || t('status.unknown');

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const itemCount = order.items.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <div className="bg-card border border-primary/10 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-primary/10 bg-background/50">
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div>
              <p className="text-xs text-muted-foreground">{t('orders.order_number')}</p>
              <p className="font-semibold text-foreground text-sm sm:text-base">{order.id}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t('orders.order_date')}</p>
              <p className="text-xs sm:text-sm text-foreground">{order.orderDate}</p>
            </div>
          </div>
          <div className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full ${statusStyle.bgColor}`}>
            <StatusIcon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${statusStyle.color}`} />
            <span className={`text-xs sm:text-sm font-medium ${statusStyle.color}`}>{statusLabel}</span>
          </div>
        </div>
      </div>

      {/* Items List - showing rows with name, quantity, price */}
      <div className="p-4">
        <div className="space-y-3 mb-4">
          {order.items.map(({ product, quantity }) => (
            <div key={product.id} className="flex items-center gap-3">
              <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-background">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23666"><rect width="24" height="24" fill="%23333"/><text x="12" y="16" text-anchor="middle" font-size="8" fill="%23666">?</text></svg>';
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
                <p className="text-xs text-muted-foreground">
                  {quantity > 1 ? `${quantity}x ` : ''}{formatPrice(product.price)}
                </p>
              </div>
              <p className="text-sm font-medium text-foreground">
                {formatPrice(product.price * quantity)}
              </p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-3 border-t border-primary/10">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Package className="w-4 h-4" />
              {itemCount} {itemCount !== 1 ? t('orders.items_plural') : t('orders.item')}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">{t('orders.delivery')}:</span> {order.deliveryDate}
            </span>
          </div>
          <p className="font-bold text-foreground text-right">{formatPrice(order.total)}</p>
        </div>
      </div>
    </div>
  );
}

export default function OrderHistoryPage() {
  const { orders, getCartCount, items: cartItems } = useCart();
  const { sendContext } = useVoice();
  const { t } = useLanguage();

  // Send rich context when page loads - include FULL order details
  useEffect(() => {
    const cartTotal = cartItems.reduce((sum, i) => sum + i.product.price * i.quantity, 0);

    // Build comprehensive order info for voice agent
    const orderDetails = orders.map(order => {
      const effectiveStatus = getEffectiveStatus(order);
      const statusLabel = t(`status.${effectiveStatus}`) || t('status.unknown');
      const itemCount = order.items.reduce((acc, item) => acc + item.quantity, 0);
      const itemList = order.items.map(i => `${i.quantity}x ${i.product.name} (€${i.product.price})`).join(', ');

      // What actions are available for this order?
      const orderActions: string[] = [];
      if (effectiveStatus === 'delivered') {
        orderActions.push('Retour aanvragen');
        orderActions.push('Opnieuw bestellen');
      } else if (effectiveStatus === 'shipped') {
        orderActions.push('Volgen via tracking');
      } else if (effectiveStatus === 'processing') {
        orderActions.push('Annuleren');
      }

      return `BESTELLING ${order.id}:
  Status: ${statusLabel}
  Besteld op: ${order.orderDate}
  Bezorging: ${order.deliveryDate}
  Totaal: €${order.total.toFixed(2)} (${itemCount} artikelen)
  Producten: ${itemList}
  Mogelijke acties: ${orderActions.join(', ') || 'geen'}`;
    });

    sendContext({
      page: 'bestellingen',

      // Cart info
      cart_count: getCartCount(),
      cart_total: cartTotal,
      cart_items: cartItems.map(i => ({
        id: i.product.id,
        name: i.product.name,
        price: i.product.price,
        quantity: i.quantity
      })),

      // All orders with full details
      visible_orders: orderDetails,

      // Summary of what's on screen
      visible_products: orders.length > 0
        ? [`${orders.length} bestellingen zichtbaar`]
        : ['Geen bestellingen gevonden'],

      // Available actions
      available_actions: orders.length > 0 ? [
        'Bestelling details bekijken',
        'Retour aanvragen (voor bezorgde bestellingen)',
        'Tracking bekijken (voor verzonden bestellingen)',
        'Opnieuw bestellen',
        'Terug naar winkel',
      ] : [
        'Naar de winkel gaan',
        'Producten bekijken',
      ],

      // Navigation
      navigation: [
        'Home / Winkel',
        'Winkelwagen' + (getCartCount() > 0 ? ` (${getCartCount()} items)` : ''),
      ],
    });
  }, [sendContext, getCartCount, orders, cartItems]);

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center">
            <Package className="w-24 h-24 mx-auto text-muted-foreground mb-6" />
            <h1 className="text-2xl font-bold text-foreground mb-4">{t('orders.no_orders')}</h1>
            <p className="text-muted-foreground mb-8">
              {t('orders.no_orders_desc')}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('orders.go_to_store')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/"
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-card transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold text-foreground">{t('orders.title')}</h1>
        </div>

        <div className="space-y-4">
          {orders.map(order => (
            <OrderCard key={order.id} order={order} t={t} />
          ))}
        </div>
      </div>
    </div>
  );
}
